import type { ReactNode } from 'react'
import { useTranslation } from 'react-i18next'

import { StatCardGrid } from './StatCard'

/**
 * L'ÉTAT DE CHARGEMENT UNIQUE (LOT-H10).
 *
 * ══════════════════════════════════════════════════════════════════════════
 * POURQUOI CE FICHIER EXISTE
 * ══════════════════════════════════════════════════════════════════════════
 *
 * La recette DA a mesuré, captures horodatées de la même visite à l'appui
 * (DA-004) : **pas de titre avant ~20 s** sur `/dashboard-analytics`, ~10 s sur
 * `/calls`. Pendant ce temps l'agent regarde un rond qui tourne au milieu d'un
 * grand vide — sans titre, sans structure, sans rien qui lui dise où il est.
 *
 * Pire, là où la page rendait quand même sa bande de KPI, elle rendait
 * `0`. Un `0` n'est pas une absence de valeur, c'est une **affirmation** :
 * l'écran annonçait « zéro campagne » au-dessus d'une liste qui disait
 * « Chargement… ». C'est le sens du verdict de la recette — « un produit qui
 * affiche un chiffre faux n'est pas à finir, il est non livrable ».
 *
 * ══════════════════════════════════════════════════════════════════════════
 * LES DEUX RÈGLES
 * ══════════════════════════════════════════════════════════════════════════
 *
 * 1. **Jamais un spinner plein écran.** Un spinner dit « attends » ; un
 *    squelette dit « voici ce qui arrive, et où ». Le `PageHeader` se rend
 *    AVANT la donnée : le titre est à l'écran en moins d'une seconde même
 *    quand les chiffres mettent 20 s.
 *
 * 2. **Les cotes du squelette sont celles du composant FINAL.** Une silhouette
 *    plus haute ou plus basse que le contenu réel fait sauter la page au
 *    moment où la donnée arrive — on aurait remplacé un vide par un
 *    tremblement. Les blocs ci-dessous reproduisent au pixel le `StatCard` de
 *    H4 (**68 px**) et la ligne de `DataTable` de H5.
 *
 * Le squelette porte `role="status"` + `aria-busy` : un lecteur d'écran ne
 * voit pas une silhouette grise, il a besoin qu'on lui dise que ça charge.
 * C'est ce qui remplace le mot « Chargement… » qu'on retire de l'écran.
 */

/**
 * Bloc gris élémentaire. `bg-background-secondary` (slate-800) : la même
 * surface que les fonds surélevés du produit, sans dégradé — un dégradé ne
 * porte aucun rôle (§1.1), pas même celui de « ça charge ».
 */
export function Skeleton({ className = '' }: { className?: string }) {
  return <div aria-hidden="true" className={`animate-pulse rounded bg-background-secondary ${className}`.trim()} />
}

/**
 * Silhouette d'UNE carte de statistique — **68 px**, comme `StatCard`.
 *
 * Le calcul, pour que la prochaine retouche puisse le refaire :
 *
 *     bordure 1 + p-3 12
 *   + libellé  t6 → interligne 14   (`h-3.5`)
 *   + mt-1                       4
 *   + valeur   t2 → interligne 24   (`h-6`)
 *   + p-3 12 + bordure 1
 *   ────────────────────────────────
 *   = 68 px
 *
 * `h-3` (12 px) et `h-5` (20 px) auraient donné 62 px : 6 px de saut à
 * l'arrivée de la donnée, sur toute la rangée à la fois.
 */
export function StatCardSkeleton() {
  return (
    <div className="flex items-center gap-2 rounded-card border border-border bg-background-primary p-3">
      <Skeleton className="h-8 w-8 shrink-0 rounded-control" />
      <div className="min-w-0 flex-1">
        <Skeleton className="h-3.5 w-20" />
        <Skeleton className="mt-1 h-6 w-16" />
      </div>
    </div>
  )
}

/**
 * Silhouette d'une BANDE de KPI.
 *
 * Elle passe par `StatCardGrid` et non par une grille écrite à la main : c'est
 * la grille qui décide de ses colonnes d'après le nombre de cartes (§5bis), et
 * une silhouette qui choisirait les siennes se réorganiserait sous les yeux de
 * l'utilisateur au moment du chargement — 6 cartes en 3 colonnes puis en 6.
 */
export function StatBandSkeleton({ count = 4, className = '' }: { count?: number; className?: string }) {
  return (
    <StatCardGrid className={className}>
      {Array.from({ length: count }, (_, i) => (
        <StatCardSkeleton key={i} />
      ))}
    </StatCardGrid>
  )
}

/**
 * Silhouette d'un TABLEAU — même chrome que `DataTable` (H5) : carte bordée,
 * en-tête sur fond surélevé, lignes séparées par `divide-border`, densité
 * `px-3 py-2`.
 *
 * `rows` par défaut à 6 : assez pour que la zone occupe une hauteur crédible,
 * pas assez pour qu'un tableau qui reviendra vide ait l'air d'avoir perdu ses
 * données.
 */
export function TableSkeleton({
  rows = 6,
  columns = 4,
  withActions = false,
  className = '',
}: {
  rows?: number
  columns?: number
  withActions?: boolean
  className?: string
}) {
  const total = withActions ? columns + 1 : columns

  return (
    <div className={`overflow-hidden rounded-card border border-border bg-background-primary ${className}`.trim()}>
      <div className="border-b border-border bg-background-secondary/40 px-3 py-2">
        <div className="flex gap-3">
          {Array.from({ length: total }, (_, i) => (
            <Skeleton key={i} className="h-3.5 flex-1" />
          ))}
        </div>
      </div>
      <div className="divide-y divide-border">
        {Array.from({ length: rows }, (_, r) => (
          <div key={r} className="flex items-center gap-3 px-3 py-2">
            {Array.from({ length: columns }, (_, c) => (
              <Skeleton key={c} className="h-4 flex-1" />
            ))}
            {/* Une action de ligne fait 44 px (§5quinquies n°3) : c'est elle qui
                fixe la hauteur d'une ligne à 60 px. Si la silhouette l'oubliait,
                le tableau grandirait de 24 px par ligne à l'arrivée des données. */}
            {withActions && <Skeleton className="h-11 w-11 shrink-0 rounded-control" />}
          </div>
        ))}
      </div>
    </div>
  )
}

/**
 * Silhouette d'une GRILLE DE CARTES — `/contacts` et `/campaigns` listent en
 * cartes, pas en tableau. Hauteur volontairement générique : ces cartes n'ont
 * pas de gabarit unique (elles relèvent de H12), donc la silhouette ne
 * prétend pas les reproduire au pixel — elle occupe leur place.
 */
export function CardGridSkeleton({
  count = 6,
  height = 'h-40',
  className = '',
}: {
  count?: number
  height?: string
  className?: string
}) {
  return (
    <div className={`grid gap-4 sm:grid-cols-2 xl:grid-cols-3 ${className}`.trim()}>
      {Array.from({ length: count }, (_, i) => (
        <Skeleton key={i} className={`${height} rounded-card`} />
      ))}
    </div>
  )
}

/**
 * Silhouette d'un GRAPHIQUE — un bloc à la hauteur EXACTE du `ChartWrapper`
 * qu'il remplace. La hauteur passe par `style` et non par une classe : elle
 * varie d'une carte à l'autre (150, 180, 220…) et une classe composée à
 * l'exécution (`h-[${n}px]`) n'est jamais générée par Tailwind.
 */
export function ChartSkeleton({ height = 180, className = '' }: { height?: number; className?: string }) {
  return (
    <div className={className} style={{ height }}>
      <Skeleton className="h-full w-full rounded-card" />
    </div>
  )
}

export interface LoadingStateProps {
  /** Nombre de cartes de la bande de KPI. `0` ⇒ pas de bande. */
  statCards?: number
  /** Nombre de lignes de tableau. `0` ⇒ pas de tableau. */
  rows?: number
  /** Nombre de colonnes du tableau. */
  columns?: number
  /** Le tableau porte-t-il une colonne d'actions ? (44 px, donc lignes de 60 px) */
  withActions?: boolean
  /**
   * Libellé annoncé aux lecteurs d'écran. Par défaut `common:states.loading`.
   * C'est la SEULE trace textuelle du chargement : à l'écran, il n'y a que des
   * silhouettes.
   */
  label?: string
  className?: string
  children?: ReactNode
}

/**
 * Assemblage courant : une bande de KPI puis un tableau.
 *
 * Les pages qui ont une autre anatomie composent directement les briques
 * ci-dessus — elles restent exportées pour ça.
 */
export function LoadingState({
  statCards = 4,
  rows = 6,
  columns = 4,
  withActions = false,
  label,
  className = '',
  children,
}: LoadingStateProps) {
  const { t } = useTranslation('common')

  return (
    <div
      role="status"
      aria-busy="true"
      aria-label={label ?? t('states.loading')}
      className={`space-y-4 ${className}`.trim()}
    >
      {statCards > 0 && <StatBandSkeleton count={statCards} />}
      {children}
      {rows > 0 && <TableSkeleton rows={rows} columns={columns} withActions={withActions} />}
    </div>
  )
}

export default LoadingState
